import React, { useRef, useState } from 'react';
import { IconCheck, IconLayoutKanban, IconX } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import type { BoardStage } from '../../../entities/board';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { type BoardForm, COLOR_PRESETS, EMPTY_BOARD_FORM } from '../model/types';

interface BoardModalProps {
  mode: 'add' | 'edit';
  initial?: BoardStage | null;
  isSystem?: boolean;
  finishMinOrder?: number | null;
  insertOrder?: number;
  onClose: () => void;
  onSaved: () => void;
}

const BoardModal: React.FC<BoardModalProps> = ({ mode, initial, isSystem = false, finishMinOrder = null, insertOrder, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<BoardForm>({
    defaultValues:
      mode === 'edit' && initial
        ? {
            name: initial.name ?? '',
            actual: initial.actual ?? true,
            colorHeaderBg: initial.colors?.headerBg ?? '#FFF3E0',
            colorDotBg: initial.colors?.dotBg ?? '#FF9800',
            colorNameColor: initial.colors?.nameColor ?? '#E65100',
            colorCntBg: initial.colors?.cntBg ?? '#FFE0B2',
            colorCntColor: initial.colors?.cntColor ?? '#E65100',
          }
        : EMPTY_BOARD_FORM,
  });

  const actual = watch('actual');
  const colorHeaderBg = watch('colorHeaderBg');
  const colorDotBg = watch('colorDotBg');
  const colorNameColor = watch('colorNameColor');
  const colorCntBg = watch('colorCntBg');
  const colorCntColor = watch('colorCntColor');
  const stageName = watch('name');

  const applyPreset = (preset: typeof COLOR_PRESETS[number]) => {
    Object.entries(preset.values).forEach(([key, val]) => {
      setValue(key as keyof BoardForm, val, { shouldDirty: true });
    });
  };

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      const orderForCreate = insertOrder !== undefined
        ? insertOrder
        : finishMinOrder !== null ? finishMinOrder - 1 : undefined;
      const payload = isSystem
        ? { name: values.name.trim() }
        : {
            name: values.name.trim(),
            ...(mode === 'add' && orderForCreate !== undefined ? { order: orderForCreate } : {}),
            actual: values.actual,
            colors: {
              headerBg: values.colorHeaderBg,
              dotBg: values.colorDotBg,
              nameColor: values.colorNameColor,
              cntBg: values.colorCntBg,
              cntColor: values.colorCntColor,
            },
          };
      if (mode === 'add') {
        await pb.collection('board').create(payload);
        logAction(`Created stage: ${values.name.trim()}`, 'ADD');
      } else {
        await pb.collection('board').update(initial!.id, payload);
        logAction(`Edited stage: ${values.name.trim()}`, 'EDIT');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 560 }}>
        <div className="cm-top">
          <div className="cm-ti">
            {mode === 'add' ? 'Добавить колонку' : isSystem ? 'Системная колонка' : 'Редактировать колонку'}
          </div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 16 }}>
          <div className="adm-field">
            <label className="adm-label">
              Название <span className="adm-req">*</span>
            </label>
            <div className="adm-input-wrap">
              <IconLayoutKanban size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input"
                type="text"
                placeholder="Например, Новый лид"
                autoComplete="off"
                {...register('name', {
                  required: 'Укажите название колонки',
                  validate: value => value.trim() !== '' || 'Укажите название колонки',
                })}
              />
            </div>
            {errors.name?.message ? <div className="form-field-error">{errors.name.message}</div> : null}
          </div>
          {!isSystem && (
            <div className="adm-field">
              <label className="adm-label">Статус</label>
              <button
                type="button"
                className={`adm-toggle${actual ? ' on' : ''}`}
                onClick={() => setValue('actual', !actual, { shouldDirty: true })}
              >
                <span className="adm-toggle-knob" />
                <span className="adm-toggle-label">{actual ? 'Активно' : 'Отключено'}</span>
              </button>
            </div>
          )}

          {/* Colors */}
          {!isSystem && <div className="adm-colors-section">
            <div className="adm-colors-header">
              <span className="adm-label">Цвета колонки</span>
            </div>
            <div className="adm-color-presets">
              {COLOR_PRESETS.map(preset => (
                <button
                  key={preset.label}
                  type="button"
                  className="adm-color-preset"
                  title={preset.label}
                  style={{ background: preset.dot }}
                  onClick={() => applyPreset(preset)}
                />
              ))}
            </div>
            <div className="adm-color-preview" style={{ background: colorHeaderBg }}>
              <div className="adm-color-preview-dot" style={{ background: colorDotBg }} />
              <div className="adm-color-preview-name" style={{ color: colorNameColor }}>
                {stageName || 'Название колонки'}
              </div>
              <div className="adm-color-preview-cnt" style={{ background: colorCntBg, color: colorCntColor }}>
                12
              </div>
            </div>
            <div className="adm-color-rows">
              {([
                { field: 'colorHeaderBg', label: 'Фон шапки', val: colorHeaderBg },
                { field: 'colorDotBg', label: 'Точка', val: colorDotBg },
                { field: 'colorNameColor', label: 'Текст названия', val: colorNameColor },
                { field: 'colorCntBg', label: 'Фон счётчика', val: colorCntBg },
                { field: 'colorCntColor', label: 'Цвет счётчика', val: colorCntColor },
              ] as const).map(({ field, label, val }) => (
                <div key={field} className="adm-color-row">
                  <label className="adm-color-row-label">{label}</label>
                  <div className="adm-color-row-right">
                    <div className="adm-color-swatch" style={{ background: val }}>
                      <input type="color" className="adm-color-input" {...register(field)} />
                    </div>
                    <span className="adm-color-hex">{val}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>}

          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { BoardModal };
